import {
  BoldIcon,
  Code2Icon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
} from "lucide-react";
import { useRef } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  applyTemplateDescriptionFormat,
  TemplateDescriptionFormatAction,
} from "@/lib/template-description";

type FormattingControl = {
  action: TemplateDescriptionFormatAction;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
};

const formattingControls: FormattingControl[] = [
  { action: "bold", label: "Bold", Icon: BoldIcon },
  { action: "italic", label: "Italic", Icon: ItalicIcon },
  { action: "bullet", label: "Bullet list", Icon: ListIcon },
  { action: "number", label: "Numbered list", Icon: ListOrderedIcon },
  { action: "quote", label: "Quote", Icon: QuoteIcon },
  { action: "code", label: "Code", Icon: Code2Icon },
  { action: "link", label: "Link", Icon: LinkIcon },
];

export const TemplateDescriptionEditor = ({
  id,
  value,
  onChange,
  placeholder,
  minHeightClassName = "min-h-[96px]",
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  minHeightClassName?: string;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleFormatAction = (action: TemplateDescriptionFormatAction) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const formatted = applyTemplateDescriptionFormat(
      value,
      textarea.selectionStart,
      textarea.selectionEnd,
      action,
    );

    onChange(formatted.value);

    requestAnimationFrame(() => {
      const nextTextarea = textareaRef.current;
      if (!nextTextarea) {
        return;
      }

      nextTextarea.focus();
      nextTextarea.setSelectionRange(
        formatted.selectionStart,
        formatted.selectionEnd,
      );
    });
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>Description</Label>
        <span className="text-[11px] text-muted-foreground">
          Basic formatting + Markdown supported
        </span>
      </div>

      <div className="flex flex-wrap gap-1">
        {formattingControls.map(({ action, label, Icon }) => (
          <Button
            key={action}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 rounded-md px-2 text-[11px]"
            onClick={() => handleFormatAction(action)}
            title={label}
          >
            <Icon className="size-3.5" />
            {label}
          </Button>
        ))}
      </div>

      <Textarea
        id={id}
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={minHeightClassName}
      />
      <p className="text-[11px] text-muted-foreground">
        Supports headings, lists, links, inline code, and emphasis.
      </p>
    </div>
  );
};
